import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowRight, FiCheck, FiCreditCard, FiShield, FiStar } from 'react-icons/fi';
import api from '../axios';
import { hasActiveSubscription } from '../utils/onboarding';

const plans = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: 49,
    billing: 'Per month',
    description: 'Full platform access with flexible month-to-month billing after your trial.',
    features: [
      'Configure forex and crypto symbol lists',
      'Set max trades and max pending orders',
      'Define daily profit and loss limits',
      'Adjust weekday and weekend trade caps',
    ],
    popular: true,
  },
  {
    id: 'yearly',
    name: 'Yearly',
    price: 490,
    billing: 'Per year',
    description: 'Full platform access with annual billing and a lower effective monthly cost.',
    features: [
      'Configure forex and crypto symbol lists',
      'Set max trades and max pending orders',
      'Define daily profit and loss limits',
      'Lower effective cost at $40.83/month',
    ],
    popular: false,
  },
];

function Subscription() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [error, setError] = useState('');
  const [subscriptionInfo, setSubscriptionInfo] = useState({
    status: null,
    hasTrial: false,
  });

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const response = await api.get('/api/check_subscription');
        const status = response.data?.status || 'inactive';
        setSubscriptionInfo({
          status,
          hasTrial: Boolean(response.data?.has_trial) || ['trial', 'trialing'].includes(status),
        });
      } catch (err) {
        console.error('Error checking subscription:', err);
      }
    };

    checkSubscription();
  }, []);

  const handleSelectPlan = async (planId) => {
    setLoading(true);
    setSelectedPlan(planId);
    setError('');

    try {
      const response = await api.post('/api/subscription/create-intent', {
        plan_id: planId,
      });

      if (response.data?.client_secret) {
        navigate('/checkout', {
          state: {
            clientSecret: response.data.client_secret,
            paymentIntentId: response.data.payment_intent_id,
            planId,
            amount: response.data.amount,
            planName: plans.find((plan) => plan.id === planId)?.name,
          },
        });
      } else {
        setError('Failed to create payment intent.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to process plan selection.');
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  return (
    <div className="min-h-screen overflow-hidden bg-[#07111a] text-white">
      <div className="fixed inset-0 -z-20 bg-[radial-gradient(circle_at_top_left,_rgba(27,186,123,0.16),_transparent_28%),radial-gradient(circle_at_80%_20%,_rgba(45,122,255,0.16),_transparent_30%),linear-gradient(180deg,_#09131d_0%,_#05080c_100%)]" />

      <div className="mx-auto max-w-7xl px-4 pb-16 pt-28 sm:px-6 lg:px-8 lg:pt-32">
        <section className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-end">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-4 py-2 text-sm font-semibold text-emerald-200">
              <FiCreditCard className="h-4 w-4" />
              Choose how you want to be billed after the trial
            </div>
            <h1 className="mt-6 max-w-4xl text-5xl font-black leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl">
              Choose the billing option
              <span className="block bg-gradient-to-r from-emerald-300 via-cyan-300 to-sky-400 bg-clip-text text-transparent">
                that fits your workflow
              </span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300 sm:text-xl">
              Your trial begins at registration. When you are ready to continue, choose either monthly flexibility or yearly savings.
            </p>
          </div>

          <div className="rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur-xl">
            <p className="text-sm uppercase tracking-[0.24em] text-cyan-200/70">Current account state</p>
            <div className="mt-4 space-y-3 text-sm text-slate-300">
              <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-4">
                <span className="text-slate-400">Subscription status:</span>{' '}
                <span className="font-semibold capitalize text-white">{subscriptionInfo.status || 'Checking...'}</span>
              </div>
              <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-4">
                <span className="text-slate-400">Trial active:</span>{' '}
                <span className="font-semibold text-white">{subscriptionInfo.hasTrial ? 'Yes' : 'No / Unknown'}</span>
              </div>
              <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-4 leading-7">
                Your funds remain with your broker. Billing changes platform access timing, not broker custody.
              </div>
            </div>
          </div>
        </section>

        {error && (
          <div className="mt-8 rounded-2xl border border-rose-300/20 bg-rose-300/10 p-4 text-sm text-rose-200">
            {error}
          </div>
        )}

        <section className="mt-10 grid gap-6 xl:grid-cols-2">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative overflow-hidden rounded-[2rem] border p-7 backdrop-blur-xl transition duration-300 hover:-translate-y-1 ${plan.popular ? 'border-cyan-300/35 bg-slate-950/80 shadow-[0_22px_70px_rgba(34,211,238,0.16)]' : 'border-white/10 bg-white/5'}`}
            >
              {plan.popular && (
                <div className="absolute right-6 top-6 rounded-full border border-emerald-300/25 bg-emerald-300/12 px-3 py-1 text-xs font-bold uppercase tracking-[0.22em] text-emerald-200">
                  Recommended
                </div>
              )}

              <p className="text-sm uppercase tracking-[0.24em] text-cyan-200/70">{plan.billing}</p>
              <h2 className="mt-4 text-3xl font-black text-white">{plan.name}</h2>
              <div className="mt-4 flex items-end gap-2">
                <span className="text-5xl font-black text-white">${plan.price}</span>
                <span className="pb-1 text-sm text-slate-400">{plan.id === 'yearly' ? '/year' : '/month'}</span>
              </div>
              <p className="mt-4 text-sm leading-7 text-slate-400">{plan.description}</p>

              <div className="mt-6 space-y-3 rounded-[1.5rem] border border-white/10 bg-slate-950/60 p-5">
                {plan.features.map((feature) => (
                  <div key={feature} className="flex items-start gap-3 text-sm text-slate-300">
                    <FiCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-300" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => handleSelectPlan(plan.id)}
                disabled={loading && selectedPlan === plan.id}
                className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl px-6 py-4 font-bold transition ${plan.popular ? 'bg-gradient-to-r from-emerald-300 to-cyan-300 text-slate-950 hover:scale-[1.01]' : 'border border-white/12 bg-white/5 text-white hover:bg-white/10'} disabled:opacity-60`}
              >
                {loading && selectedPlan === plan.id ? 'Processing...' : `Choose ${plan.name}`}
                {!(loading && selectedPlan === plan.id) && <FiArrowRight className="h-4 w-4" />}
              </button>
            </div>
          ))}
        </section>

        {hasActiveSubscription(subscriptionInfo.status) && (
          <section className="mt-8 rounded-[2rem] border border-emerald-300/20 bg-emerald-300/10 p-6 text-emerald-100 backdrop-blur-xl">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-2xl font-black text-white">Subscription already active</h2>
                <p className="mt-2 max-w-2xl text-sm leading-7 text-emerald-100/90">
                  Your account already has access. Continue to your account area to finish MT5 connection and trading setup.
                </p>
              </div>
              <button
                onClick={() => navigate('/account')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-bold text-slate-950 transition hover:scale-[1.01]"
              >
                Continue setup
                <FiArrowRight className="h-4 w-4" />
              </button>
            </div>
          </section>
        )}

        <section className="mt-10 rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur-xl lg:p-8">
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="rounded-[1.5rem] border border-white/10 bg-slate-950/60 p-5">
              <div className="inline-flex rounded-2xl border border-cyan-300/20 bg-cyan-300/10 p-3 text-cyan-200">
                <FiShield className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-xl font-bold text-white">Secure checkout path</h3>
              <p className="mt-3 text-sm leading-7 text-slate-400">Both billing options move into the dedicated checkout route rather than trying to process payment inline here.</p>
            </div>
            <div className="rounded-[1.5rem] border border-white/10 bg-slate-950/60 p-5">
              <div className="inline-flex rounded-2xl border border-cyan-300/20 bg-cyan-300/10 p-3 text-cyan-200">
                <FiStar className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-xl font-bold text-white">Trial-first logic</h3>
              <p className="mt-3 text-sm leading-7 text-slate-400">The product still encourages validation before commitment. The trial is the starting point and billing starts only when you choose to continue.</p>
            </div>
            <div className="rounded-[1.5rem] border border-white/10 bg-slate-950/60 p-5">
              <div className="inline-flex rounded-2xl border border-cyan-300/20 bg-cyan-300/10 p-3 text-cyan-200">
                <FiCreditCard className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-xl font-bold text-white">Pricing consistency</h3>
              <p className="mt-3 text-sm leading-7 text-slate-400">This screen now shares the same cadence-based pricing model and language as the public pricing page.</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Subscription;
